import AxiosInstance from '../../core/api/axiosinstance'
import { promiseToResult, querystring } from '../../utils'
import { AuthType, TransactionStatus, TransactionType } from '../../shared/enum'
import { GetReferralsReq, User } from './User'
import { Content } from './_type'

export interface YandexMetadata {
  type: 'yandex'
  wallet: string
}
export interface CryptoMetadata {
  type: 'crypto'
  wallet: string
  network: 'trc20'
  currencyId: string
}

export interface SinglePaymentTransaction {
  type: TransactionType.SINGLE_PAYMENT
  projectSymbol: string
  shares: number
  paymentSystemId?: number | null
}
export interface InstallmentOpenTransaction {
  type: TransactionType.INSTALLMENT_OPEN
  projectSymbol: string
  shares: number
  months: number
  paymentSystemId?: number | null
}
export interface InstallmentPaymentTransaction {
  type: TransactionType.INSTALLMENT_PAYMENT
  installmentId: number
  payment: number
  paymentSystemId?: number | null
}
export interface WithdrawalTransaction {
  type: TransactionType.WITHDRAW
  payment: number
  paymentSystemId: number
  validationCode: string
  sendCodeType: AuthType
  image?: string
  metadata: YandexMetadata | CryptoMetadata
}
export interface AddBalanceTransaction {
  type: TransactionType.BALANCE
  paymentSystemId: number
  payment: number
}
export interface TransferTransaction {
  type: TransactionType.TRANSFER_BALANCE
  userId: number
  payment: number
  validationCode: string
  sendCodeType: AuthType
}
export interface Transaction {
  id: number
  type: TransactionType
  user?: User | number | null
  nftId: number | null
  linkUser: null | { type: string, userId: number }
  installment: { id: number }
  paymentSystem: { id: number, icon: string, title: Content[] }
  project?: { id: number, name: string }
  moneyboxId?: number | null
  price: number
  shares: 0
  comment?: string
  status: TransactionStatus
  createdAt: string
  updatedAt: string
  metadata: null | {
    referralDepth?: number
    receivedTransactionId?: number
    type?: string
    wallet?: string
    network?: string
    currencyId?: string
    moneyBoxId?: number
  }
}
export interface ReferralTransaction extends Omit<Transaction, 'user' | 'metadata'> {
  type: TransactionType.REFERRAL_PRIZE
  status: TransactionStatus.COMPLETED
  user: User | null
  metadata?: { referralDepth?: number }
}
export interface Installment {
  id: number
  user: number
  description: string
  isPaid: boolean
  project: number
  price: number
  shares: number
  minimumPayment: number
  comment?: string
  createdAt: string
  updatedAt: string
  paidAt: string
  nextPaymentDate: string
  closedAt?: string
  calendar: {
    isPaid: boolean
    month: number
    price: number
    shares: number
    transactionId: number
  }[]
  payPeriod: {
    months: number
    giftShares: number
  }
}
export interface Nft {
  id: number
  blockNumber: number
  transactionHash: string
  templateName: string
  nftId: number
  contractAddress: string
  to: string
  chain: string
  metadata: {
    date?: string
    id: number
    image?: string
    name?: string
    project?: string
    shares?: number
    uri: string
    owner?: string
    author?: string
  }
  createdAt: string
  updatedAt: string
}

export interface PostTransactionsReq {
  transaction:
    | SinglePaymentTransaction
    | InstallmentPaymentTransaction
    | WithdrawalTransaction
    | AddBalanceTransaction
    | TransferTransaction
  fullExternal?: boolean
}
export interface PostTransactionsRes {
  transaction: Transaction
  paymentInfo?: {
    redirectUrl: string
    status: true
  }
}
const postTransactions = (data: PostTransactionsReq) => {
  return promiseToResult(
    AxiosInstance().post<PostTransactionsRes>(
      '/projects/transactions',
      data,
    ),
  )
}

export type PostInstallmentsReq = InstallmentOpenTransaction
export interface PostInstallmentsRes {
  installment: Installment
  transaction: Transaction
  payingTransaction: Transaction
  paymentInfo?: {
    redirectUrl: string
    status: true
  }
}
const postInstallments = (data: PostInstallmentsReq) => {
  return promiseToResult(
    AxiosInstance().post<PostInstallmentsRes>(
      '/projects/installments',
      data,
    ),
  )
}

export interface GetMyTransactionsReq {
  limit: number
  page: number
  type?: TransactionType[]
  status?: TransactionStatus[]
  project?: number[] | string[]
  referralId?: number
}
export interface GetMyTransactionsRes {
  items: Transaction[]
  metadata: {
    limit: number
    page: number
    total: number
  }
}
const getMyTransactions = (data: GetMyTransactionsReq) => {
  return promiseToResult(
    AxiosInstance().get<GetMyTransactionsRes>(
      `/projects/transactions/@my?${querystring.stringify(data)}`,
    ),
  )
}

export interface GetTransactionReq {
  id: number | string
}
export type GetTransactionRes = Transaction
const getTransaction = (data: GetTransactionReq) => {
  return promiseToResult(
    AxiosInstance().get<GetTransactionRes>(
      `/projects/transactions/${data.id}`,
    ),
  )
}

export interface GetMyInstallmentsRes {
  items: Installment[]
}
const getMyInstallments = () => {
  return promiseToResult(
    AxiosInstance().get<GetMyInstallmentsRes>(
      '/projects/installments/@my',
    ),
  )
}

export interface GetMyInstallmentReq {
  id: number
}
export type GetMyInstallmentRes = Installment
const getMyInstallment = (data: GetMyInstallmentReq) => {
  const { id } = data
  return promiseToResult(
    AxiosInstance().get<GetMyInstallmentRes>(
      `/projects/installments/@my/${id}`,
    ),
  )
}

export type PutCancelInstallmentReq = GetMyInstallmentReq
const putCancelInstallment = (data: PutCancelInstallmentReq) => {
  const { id } = data
  return promiseToResult(
    AxiosInstance().put(
      `/projects/installments/@my/${id}/cancel`,
    ),
  )
}

export interface GetMyReferralsTransactionsReq {
  limit: number
  page: number
}
export interface GetMyReferralsTransactionsRes {
  items: ReferralTransaction[]
  metadata: {
    total: number
    page: number
    limit: number
  }
}
const getMyReferralsTransactions = (data: GetMyReferralsTransactionsReq) => {
  return promiseToResult(
    AxiosInstance().get<GetMyReferralsTransactionsRes>(
      `/projects/referrals/@my/transactions?${querystring.stringify(data)}`,
    ),
  )
}

export interface PostPayReq {
  id: number | string
}
export interface PostPayRes {
  redirectUrl: string
  status: boolean
  raw: null
}
const postPay = (data: PostPayReq) => {
  return promiseToResult(
    AxiosInstance().post<PostPayRes>(
      `/projects/transactions/${data.id}/pay`,
    ),
  )
}

const postCancel = (data: PostPayReq) => {
  return promiseToResult(
    AxiosInstance().post(
      `/projects/transactions/${data.id}/cancel`,
    ),
  )
}

export interface GetUserSumInvestmentRes {
  sum: number
}
const getUserSumInvestment = ({ userId }: GetReferralsReq) => {
  return promiseToResult(AxiosInstance().get<GetUserSumInvestmentRes>(
    `/projects/transactions/sum-investment/${userId}`,
  ))
}

export type GetUserInstallmentsInterfaceRes = Installment[]
const getUserInstallments = ({ userId }: GetReferralsReq) => {
  return promiseToResult(AxiosInstance().get<GetUserInstallmentsInterfaceRes>(
    `/projects/installments/${userId}`,
  ))
}

export interface GetMyTransactionsStatisticReq {
  dateStart?: string
  dateEnd?: string
}
export type GetMyTransactionsStatisticRes = {
  type: TransactionType
  transactionSumPercentage: number
  transactionSumPrice: number
}[]
const getMyTransactionsStatistic = (data: GetMyTransactionsStatisticReq) => {
  return promiseToResult(AxiosInstance().get<GetMyTransactionsStatisticRes>(
    `/projects/transactions/@my/statistics?${querystring.stringify(data)}`,
  ))
}

export interface PostTransactionNftReq {
  id: number
  address: string
  templateName?: string
}
export type PostTransactionNftRes = Nft
const postTransactionNft = (data: PostTransactionNftReq) => {
  return promiseToResult(
    AxiosInstance().post<PostTransactionNftRes>(
      `/projects/transaction-nft/${data.id}`,
      data,
    ),
  )
}

export interface GetNftReq {
  ids: number[]
}
export type GetNftRes = Nft[]
const getNft = (data?: GetNftReq) => {
  return promiseToResult(
    AxiosInstance().get<GetNftRes>(
      `/projects/transaction-nft/@my?${data ? querystring.stringify(data) : ''}`,
    ),
  )
}

export interface GetTopUsersReq {
  investmentDateStart?: string
  investmentDateEnd?: string
}
export type GetTopUsersRes = {
  userId: number
  totalReferralInvestment: number
  referralIds: number[]
  rating: number
}[]
const getTopUsers = (data: GetTopUsersReq) => {
  return promiseToResult(
    AxiosInstance().get<GetTopUsersRes>(
      `/projects/transactions/top-users?${querystring.stringify(data)}`,
    ),
  )
}

export type GetTopMyUsersRes = GetTopUsersRes[0]
const getTopMyUsers = (data: GetTopUsersReq) => {
  return promiseToResult(
    AxiosInstance().get<GetTopMyUsersRes>(
      `/projects/transactions/@my-rating?${querystring.stringify(data)}`,
    ),
  )
}

export const TransactionAPI = {
  postTransactions,
  postInstallments,
  getMyTransactions,
  getTransaction,
  getMyInstallments,
  getMyInstallment,
  putCancelInstallment,
  getMyReferralsTransactions,
  postPay,
  postCancel,
  getUserSumInvestment,
  getUserInstallments,
  getMyTransactionsStatistic,
  postTransactionNft,
  getNft,
  getTopUsers,
  getTopMyUsers,
}
